/**
 * ConfirmDialog Component
 * Reusable confirmation dialog for destructive actions
 */

import { html, attr, on, prop, type Renderable, type TNode } from '@tempots/dom'
import { Button, Modal } from '@tempots/beatui'
import { Icon } from '@tempots/beatui'

// ============================================================================
// Types
// ============================================================================

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info'

export interface ConfirmDialogOptions {
  /** Title of the dialog */
  title: string
  /** Description/message to display */
  message: string | TNode
  /** Text for the confirm button */
  confirmText?: string
  /** Text for the cancel button */
  cancelText?: string
  /** Visual variant for the dialog */
  variant?: ConfirmDialogVariant
  /** Icon to display (defaults based on variant) */
  icon?: string
  /** Called when user confirms */
  onConfirm: () => void | Promise<void>
  /** Called when user cancels (optional) */
  onCancel?: () => void
}

// ============================================================================
// Variant Configuration
// ============================================================================

const variantConfig: Record<
  ConfirmDialogVariant,
  { icon: string; iconClass: string; buttonClass: string }
> = {
  danger: {
    icon: 'mdi:alert-circle',
    iconClass: 'text-red-600 bg-red-100',
    buttonClass: 'bg-red-600 hover:bg-red-700 text-white',
  },
  warning: {
    icon: 'mdi:alert',
    iconClass: 'text-yellow-600 bg-yellow-100',
    buttonClass: 'bg-yellow-600 hover:bg-yellow-700 text-white',
  },
  info: {
    icon: 'mdi:information',
    iconClass: 'text-blue-600 bg-blue-100',
    buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
}

// ============================================================================
// Component
// ============================================================================

/**
 * Confirm dialog trigger with modal
 *
 * @example
 * ```typescript
 * // Simple usage - returns a button that opens the dialog
 * ConfirmDialog({
 *   title: 'Delete File',
 *   message: 'Are you sure you want to delete this file? This action cannot be undone.',
 *   confirmText: 'Delete',
 *   variant: 'danger',
 *   onConfirm: async () => {
 *     await deleteFile(fileId)
 *   },
 * }, Button({ variant: 'outline' }, 'Delete'))
 *
 * // With custom trigger
 * ConfirmDialog({
 *   title: 'Revoke API Key',
 *   message: 'This will immediately invalidate the key.',
 *   onConfirm: handleRevoke,
 * }, html.button('Revoke Key'))
 * ```
 */
export function ConfirmDialog(
  options: ConfirmDialogOptions,
  trigger: TNode
): Renderable {
  const {
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger',
    icon,
    onConfirm,
    onCancel,
  } = options

  const config = variantConfig[variant]
  const isLoading = prop(false)

  return Modal({ size: 'sm', dismissable: true }, (open, close) =>
    html.div(
      // Trigger element - clicking opens the dialog
      html.div(
        attr.class('inline-block cursor-pointer'),
        attr.role('button'),
        attr.tabindex('0'),
        on.click(() =>
          open({
            header: html.div(
              attr.class('flex items-center gap-3'),
              html.div(
                attr.class(
                  `w-10 h-10 rounded-full flex items-center justify-center ${config.iconClass}`
                ),
                Icon({ icon: icon || config.icon }, attr.class('w-5 h-5'))
              ),
              html.h2(attr.class('text-lg font-semibold text-gray-900'), title)
            ),
            body: html.div(
              attr.class('text-sm text-gray-600'),
              typeof message === 'string' ? html.p(message) : message
            ),
            footer: html.div(
              attr.class('flex justify-end gap-3'),
              Button(
                {
                  variant: 'outline',
                  onClick: () => {
                    onCancel?.()
                    close()
                  },
                },
                cancelText
              ),
              html.button(
                attr.type('button'),
                attr.class(
                  isLoading.map(
                    (loading) =>
                      `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${config.buttonClass} ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`
                  )
                ),
                attr.disabled(isLoading),
                on.click(async () => {
                  isLoading.set(true)
                  try {
                    await onConfirm()
                    close()
                  } catch {
                    // Error handling should be done in onConfirm
                  } finally {
                    isLoading.set(false)
                  }
                }),
                isLoading.map((loading) =>
                  loading ? 'Processing...' : confirmText
                )
              )
            ),
          })
        ),
        trigger
      )
    )
  )
}

// ============================================================================
// Imperative API (for programmatic usage)
// ============================================================================

/**
 * Show a confirmation dialog imperatively (returns a Promise)
 * Note: This requires a ConfirmDialogHost to be mounted in the app
 *
 * @example
 * ```typescript
 * const confirmed = await confirm({
 *   title: 'Delete File',
 *   message: 'Are you sure?',
 *   variant: 'danger',
 * })
 * if (confirmed) {
 *   await deleteFile()
 * }
 * ```
 */

/* eslint-disable tempots/no-module-level-signals -- intentional global state for confirm dialog */
const pendingConfirm = prop<{
  options: Omit<ConfirmDialogOptions, 'onConfirm' | 'onCancel'>
  resolve: (confirmed: boolean) => void
} | null>(null)
/* eslint-enable tempots/no-module-level-signals */

export function confirm(
  options: Omit<ConfirmDialogOptions, 'onConfirm' | 'onCancel'>
): Promise<boolean> {
  return new Promise((resolve) => {
    pendingConfirm.set({ options, resolve })
  })
}

/**
 * Host component for imperative confirm dialogs
 * Mount this once at the app root level
 */
export function ConfirmDialogHost(): Renderable {
  // This would need a more complex implementation with Modal that auto-opens
  // For now, the declarative ConfirmDialog component is the recommended approach
  return html.div(
    attr.id('confirm-dialog-host'),
    attr.class('hidden')
    // Future: implement imperative dialog rendering
  )
}
